import { keepPreviousData, useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { analyticsApi, type GlobalFilters } from '@/lib/api/analytics.api';

import type {
  AvailabilityDashboardData,
  CallClassification,
  ConfiguredDashboardResponse,
  PaginatedCallsResponse,
  PaginatedCapturesResponse,
} from '@repo/shared-types';

export function useClassifiedCalls(
  globalFilters: GlobalFilters,
  classification: CallClassification,
  page: number,
  limit = 20,
  options?: Pick<UseQueryOptions<PaginatedCallsResponse, Error>, 'enabled'>,
  minRating?: number,
  excludeClassifications?: CallClassification[],
  metricKey?: string,
  excludeMetricKeys?: string[],
) {
  return useQuery<PaginatedCallsResponse, Error>({
    queryKey: [
      'analytics',
      'calls',
      globalFilters,
      classification,
      page,
      limit,
      minRating,
      excludeClassifications,
      metricKey,
      excludeMetricKeys,
    ],
    queryFn: () =>
      analyticsApi.getClassifiedCalls(
        page,
        limit,
        globalFilters,
        classification,
        minRating,
        excludeClassifications,
        metricKey,
        excludeMetricKeys,
      ),
    ...options,
  });
}

export function useAvailabilityDashboard(filters: GlobalFilters) {
  return useQuery<AvailabilityDashboardData, Error>({
    queryKey: ['analytics', 'availability', 'overview', filters],
    queryFn: () => analyticsApi.getAvailabilityDashboard(filters),
    placeholderData: keepPreviousData,
  });
}

export function useAvailabilityRecent(filters: GlobalFilters, page: number, limit = 20) {
  return useQuery<PaginatedCapturesResponse, Error>({
    queryKey: ['analytics', 'availability', 'recent', filters, page, limit],
    queryFn: () => analyticsApi.getAvailabilityRecent(page, limit, filters),
    placeholderData: keepPreviousData,
  });
}

export function useConfiguredDashboard(filters: GlobalFilters) {
  return useQuery<ConfiguredDashboardResponse, Error>({
    queryKey: ['analytics', 'configured-overview', filters],
    queryFn: () => analyticsApi.getConfiguredDashboard(filters),
    placeholderData: keepPreviousData,
  });
}
